import { useState, useEffect } from 'react'
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  AppBar,
  Toolbar,
  Avatar,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import {
  LocalPhone,
  LocationOn,
  AccessTime,
  CheckCircle,
  People,
  Healing,
  Email,
  TrendingUp,
  Menu as MenuIcon,
  Close as CloseIcon,
  ChevronRight,
} from '@mui/icons-material'

// Get business hours based on day of week
const getBusinessHours = () => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const today = new Date().getDay()
  const dayName = days[today]

  // New schedule:
  // Monday - Saturday: 9:00 AM - 10:00 PM
  // Sunday: 9:00 AM - 3:00 PM
  const isSunday = today === 0

  if (isSunday) {
    return {
      day: dayName,
      hours: '9:00 AM - 3:00 PM',
      status: 'Open',
      isClosed: false,
    }
  }

  return {
    day: dayName,
    hours: '9:00 AM - 10:00 PM',
    status: 'Open',
    isClosed: false,
  }
}

const clinicAddress =
  'Shop number 201, Second floor, Commercial Complex, HALLMARK EMPYREAN, Narsingi - Puppalaguda Main Rd, Alkapur Twp, Puppalguda, Hyderabad, Telangana 500089'

const clinicPlusCode = '99X8+P5 Hyderabad, Telangana'

export default function Home() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [businessHours, setBusinessHours] = useState(getBusinessHours())
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    setBusinessHours(getBusinessHours())
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navItems = [
    { label: 'Services', id: 'services' },
    { label: 'Why Us', id: 'whyus' },
    { label: 'Team', id: 'team' },
    { label: 'Contact', id: 'contact' },
  ]

  const handleNavClick = (id: string) => {
    setMobileOpen(false)
    // Smooth scroll implementation
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <Box>
      {/* Enhanced Navigation Header - Sticky & Responsive */}
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.95)' : '#FFFFFF',
          color: '#1A2332',
          boxShadow: scrolled ? '0 4px 12px rgba(0, 0, 0, 0.1)' : '0 1px 3px rgba(0, 0, 0, 0.08)',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          transition: 'all 0.3s ease',
          zIndex: 1000,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 2, px: { xs: 2, md: 3 } }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <Healing sx={{ color: '#00A86B', fontSize: 32 }} />
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#1A2332',
                  fontSize: { xs: '18px', md: '20px' },
                  lineHeight: 1.2,
                }}
              >
                Connect & Cure
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '11px', md: '12px' },
                  color: '#666',
                  fontWeight: 400,
                  lineHeight: 1,
                }}
              >
                Physiotherapy & Rehabilitation Center
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 3,
              alignItems: 'center',
              flex: 2,
              justifyContent: 'center',
            }}
          >
            {navItems.map((item) => (
              <Button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                sx={{
                  color: '#6B7280',
                  fontWeight: 500,
                  fontSize: '14px',
                  textTransform: 'none',
                  position: 'relative',
                  '&:hover': {
                    color: '#00A86B',
                    backgroundColor: 'transparent',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -4,
                    left: 0,
                    width: 0,
                    height: 2,
                    backgroundColor: '#00A86B',
                    transition: 'width 0.3s ease',
                  },
                  '&:hover::after': {
                    width: '100%',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          {/* Hours Status Indicator */}
          <Box
            sx={{
              display: { xs: 'none', lg: 'flex' },
              alignItems: 'center',
              gap: 1,
              mr: 2,
            }}
          >
            <AccessTime
              sx={{
                color: businessHours.isClosed ? '#EF4444' : '#00A86B',
                fontSize: 18,
              }}
            />
            <Box>
              <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>
                {businessHours.day}
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: businessHours.isClosed ? '#EF4444' : '#00A86B',
                }}
              >
                {businessHours.hours}
              </Typography>
            </Box>
          </Box>

          {/* CTA Button */}
          <Button
            component="a"
            href="tel:+917730840853"
            variant="contained"
            endIcon={<ChevronRight />}
            sx={{
              backgroundColor: '#00A86B',
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: { xs: '12px', md: '14px' },
              textTransform: 'none',
              px: { xs: 1.5, md: 3 },
              py: 1.2,
              display: { xs: 'none', sm: 'inline-flex' },
              '&:hover': { backgroundColor: '#008555' },
              transition: 'all 0.3s ease',
            }}
          >
            Book Now
          </Button>

          {/* Mobile Menu Button */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="end"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { xs: 'flex', md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: '100%',
            maxWidth: '320px',
            backgroundColor: '#F9FAFB',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A2332' }}>
              Menu
            </Typography>
            <IconButton onClick={() => setMobileOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Hours in Mobile Menu */}
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              p: 2,
              borderRadius: 1,
              mb: 3,
              border: '1px solid #E5E7EB',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccessTime sx={{ color: '#00A86B', fontSize: 18 }} />
              <Typography sx={{ fontWeight: 600, color: '#1A2332' }}>
                Today's Hours
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', color: '#6B7280', mb: 1 }}>
              {businessHours.day}
            </Typography>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: businessHours.isClosed ? '#EF4444' : '#00A86B',
              }}
            >
              {businessHours.hours}
            </Typography>
          </Box>

          {/* Mobile Nav Items */}
          <List>
            {navItems.map((item) => (
              <ListItem
                key={item.id}
                button
                onClick={() => handleNavClick(item.id)}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  '&:hover': {
                    backgroundColor: '#F0FFFA',
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: 500,
                      color: '#1A2332',
                    },
                  }}
                />
                <ChevronRight sx={{ color: '#00A86B' }} />
              </ListItem>
            ))}
          </List>

          {/* Mobile CTA */}
          <Button
            component="a"
            href="tel:+917730840853"
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: '#00A86B',
              color: '#FFFFFF',
              fontWeight: 600,
              py: 1.5,
              mt: 3,
              '&:hover': { backgroundColor: '#008555' },
            }}
          >
            Book Appointment
          </Button>
        </Box>
      </Drawer>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #00A86B 0%, #008555 100%)',
          color: '#FFFFFF',
          py: { xs: 6, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '28px', md: '42px' },
                  lineHeight: 1.3,
                }}
              >
                Recover, Rehabilitate, Reclaim Your Life
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  mb: 4,
                  opacity: 0.95,
                  lineHeight: 1.6,
                }}
              >
                Expert physiotherapy and rehabilitation services designed to get you back to doing what you love. Our certified team specializes in injury recovery, pain management, and performance enhancement.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#FFFFFF',
                    color: '#00A86B',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    '&:hover': { backgroundColor: '#F0FFFA' },
                  }}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: '#FFFFFF',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Quick Info Cards */}
      <Box sx={{ backgroundColor: '#F9FAFB', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                  border: 'none',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <LocalPhone sx={{ color: '#00A86B', fontSize: 32, mb: 1 }} />
                  <Typography sx={{ fontSize: '12px', color: '#9CA3AF', mb: 0.5 }}>
                    Phone
                  </Typography>
                  <Typography sx={{ fontWeight: 600, color: '#1A2332' }}>
                    07730840853
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                  border: 'none',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Email sx={{ color: '#00A86B', fontSize: 32, mb: 1 }} />
                  <Typography sx={{ fontSize: '12px', color: '#9CA3AF', mb: 0.5 }}>
                    Email
                  </Typography>
                  <Typography sx={{ fontWeight: 600, color: '#1A2332' }}>
                    drhafiz29@gmail.com
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                  border: 'none',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <AccessTime sx={{ color: '#00A86B', fontSize: 32, mb: 1 }} />
                  <Typography sx={{ fontSize: '12px', color: '#9CA3AF', mb: 0.5 }}>
                    Mon - Sat Hours
                  </Typography>
                  <Typography sx={{ fontWeight: 600, color: '#1A2332' }}>
                    9:00 AM - 10:00 PM
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                  border: 'none',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <AccessTime sx={{ color: '#00A86B', fontSize: 32, mb: 1 }} />
                  <Typography sx={{ fontSize: '12px', color: '#9CA3AF', mb: 0.5 }}>
                    Sunday Hours
                  </Typography>
                  <Typography sx={{ fontWeight: 600, color: '#1A2332' }}>
                    9:00 AM - 3:00 PM
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box id="services" sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              textAlign: 'center',
              color: '#1A2332',
              fontSize: { xs: '28px', md: '36px' },
            }}
          >
            Our Services
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: '#6B7280',
              mb: 6,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Comprehensive physiotherapy solutions tailored to your specific needs
          </Typography>

          <Grid container spacing={3}>
            {[
              { title: 'Sports Injury Recovery', icon: TrendingUp },
              { title: 'Post-Surgery Rehabilitation', icon: Healing },
              { title: 'Chronic Pain Management', icon: CheckCircle },
              { title: 'Athletic Performance Training', icon: People },
              { title: 'Mobility & Flexibility', icon: Healing },
              { title: 'Ergonomic Assessment', icon: TrendingUp },
            ].map((service, index) => {
              const IconComponent = service.icon
              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      border: 'none',
                      borderRadius: 2,
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 10px 25px rgba(0, 168, 107, 0.15)',
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ mb: 2 }}>
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            backgroundColor: '#F0FFFA',
                            color: '#00A86B',
                          }}
                        >
                          <IconComponent />
                        </Avatar>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: '#1A2332', mb: 1 }}
                      >
                        {service.title}
                      </Typography>
                      <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>
                        Professional treatment designed for optimal recovery and wellness.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Box id="whyus" sx={{ backgroundColor: '#F9FAFB', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 6,
              textAlign: 'center',
              color: '#1A2332',
              fontSize: { xs: '28px', md: '36px' },
            }}
          >
            Why Choose Connect & Cure
          </Typography>

          <Grid container spacing={4}>
            {[
              {
                title: 'Certified Professionals',
                description: 'Our team consists of licensed and certified physiotherapists with extensive experience.',
              },
              {
                title: 'Personalized Treatment',
                description: 'Custom care plans tailored to your specific condition and recovery goals.',
              },
              {
                title: 'State-of-the-Art Equipment',
                description: 'Access to modern rehabilitation equipment and therapy technologies.',
              },
              {
                title: 'Flexible Scheduling',
                description: 'Convenient appointment times to fit your lifestyle and recovery needs.',
              },
            ].map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <CheckCircle sx={{ color: '#00A86B', flexShrink: 0, fontSize: 28 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1A2332', mb: 1 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: '#6B7280' }}>
                      {item.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Map Section */}
      <Box sx={{ backgroundColor: '#FFFFFF', color: '#1A2332', py: 6 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 3, textAlign: 'center', color: '#1A2332' }}
          >
            Our Location
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: '100%', maxWidth: 900, height: 400, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              <iframe
                title="Clinic location"
                src={`https://www.google.com/maps?q=${encodeURIComponent(clinicPlusCode)}&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ backgroundColor: '#1A2332', color: '#FFFFFF', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Healing sx={{ color: '#00A86B', fontSize: 28 }} />
                <Typography sx={{ fontWeight: 700, fontSize: '18px' }}>
                  Connect & Cure
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '14px', color: '#D1D5DB' }}>
                Your trusted partner in rehabilitation and wellness.
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={{ fontWeight: 600, mb: 2 }}>Quick Links</Typography>
              <Typography sx={{ fontSize: '14px', color: '#D1D5DB', mb: 1 }}>
                Services
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#D1D5DB', mb: 1 }}>
                Team
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#D1D5DB' }}>
                Contact
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={{ fontWeight: 600, mb: 2 }}>Hours</Typography>
              <Typography sx={{ fontSize: '14px', color: '#D1D5DB', mb: 1 }}>
                Mon - Sat: 9:00 AM - 10:00 PM
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#D1D5DB' }}>
                Sun: 9:00 AM - 3:00 PM
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={{ fontWeight: 600, mb: 2 }}>Contact</Typography>
              <Typography sx={{ fontSize: '14px', color: '#D1D5DB', mb: 1 }}>
                07730840853
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#D1D5DB', mb: 1 }}>
                drhafiz29@gmail.com
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#D1D5DB' }}>
                {clinicAddress}
              </Typography>
            </Grid>
          </Grid>

          <Box
            sx={{
              borderTop: '1px solid #374151',
              pt: 4,
              textAlign: 'center',
              color: '#9CA3AF',
              fontSize: '14px',
            }}
          >
            <Typography>
              &copy; 2024 Connect & Cure. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  )
}
